/**
 * WhatsAppSettings — phone verification via test alert + per-event notification toggles.
 * Rendered as a modal/drawer from the user menu.
 */

import { useState, useEffect } from 'react';
import {
  Modal, Input, Button, Switch, Typography, Space, Divider,
  message, Tag, App,
} from 'antd';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, updateDoc, deleteField } from 'firebase/firestore';
import { db, app } from '@/config/firebase';
import { useEffectiveUserId } from '@/hooks/useEffectiveUserId';
import { MaterialIcon } from './MaterialIcon';

const { Text } = Typography;

// Initialize functions with us-central1 region
const functions = getFunctions(app, 'us-central1');

const sendVerificationFn = httpsCallable<
  { phoneNumber: string },
  { success: boolean }
>(functions, 'sendWhatsAppVerification');

const confirmNumberFn = httpsCallable<
  Record<string, never>,
  { success: boolean }
>(functions, 'confirmWhatsAppNumber');

interface WhatsAppData {
  phoneNumber?: string;
  verified?: boolean;
  enabledEvents?: Record<string, boolean>;
}

// ─── Event grouping ────────────────────────────────────────────
//
// Each event has its own toggle so users can independently enable the
// "something went wrong" alert and the "back to normal" notification.

interface UnifiedEvent {
  keys: string[];
  label: string;
  icon: string;
}

interface EventSection {
  label: string;
  events: UnifiedEvent[];
}

const EVENT_SECTIONS: EventSection[] = [
  {
    label: 'Connectivity',
    events: [
      { keys: ['camera_disconnected', 'camera_connected'], label: 'Camera connection', icon: 'photo_camera' },
      { keys: ['device_offline', 'device_online'], label: 'Device connection', icon: 'cloud' },
    ],
  },
  {
    label: 'Power',
    events: [
      { keys: ['charger_disconnected', 'charger_connected'], label: 'Charger connection', icon: 'power' },
      { keys: ['battery_low', 'battery_restored'], label: 'Battery level', icon: 'battery_alert' },
    ],
  },
  {
    label: 'Temperature',
    events: [
      { keys: ['battery_temperature_warning', 'battery_temperature_critical', 'battery_temperature_resolved'], label: 'Temperature', icon: 'thermostat' },
    ],
  },
];


interface WhatsAppSettingsProps {
  open: boolean;
  onClose: () => void;
}

export function WhatsAppSettings({ open, onClose }: WhatsAppSettingsProps) {
  const { effectiveUid } = useEffectiveUserId();
  const { modal } = App.useApp();
  const [whatsappData, setWhatsappData] = useState<WhatsAppData>({});
  const [phoneInput, setPhoneInput] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testSent, setTestSent] = useState(false);
  // True when the user clicked "Change number" while a verified number
  // already exists — shown so we can offer a back arrow that reverts to
  // the connected-status view without losing verification.
  const [editingNumber, setEditingNumber] = useState(false);

  // Subscribe to user's WhatsApp data
  useEffect(() => {
    if (!effectiveUid || !open) return;

    const unsubscribe = onSnapshot(
      doc(db, 'users', effectiveUid),
      (snapshot) => {
        const data = snapshot.data();
        setWhatsappData(data?.whatsapp || {});
        if (data?.whatsapp?.phoneNumber && !phoneInput) {
          setPhoneInput(data.whatsapp.phoneNumber);
        }
      }
    );

    return () => unsubscribe();
  }, [effectiveUid, open]);

  const handleSendTest = async () => {
    // Users naturally type spaces / dashes / parens — strip them so we
    // pass a clean E.164 string to the backend regex.
    const normalized = phoneInput.replace(/[\s\-()]/g, '');
    if (!normalized) {
      message.error('Enter a phone number');
      return;
    }

    setSendingTest(true);
    try {
      await sendVerificationFn({ phoneNumber: normalized });
      setPhoneInput(normalized);
      setTestSent(true);
      message.success('Test alert sent to your WhatsApp');
    } catch (error: any) {
      message.error(error.message || 'Failed to send test message');
    } finally {
      setSendingTest(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await confirmNumberFn({});
      message.success('WhatsApp number confirmed!');
      setTestSent(false);
      setEditingNumber(false);
    } catch (error: any) {
      message.error(error.message || 'Failed to confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleDisconnect = () => {
    if (!effectiveUid) return;
    modal.confirm({
      title: 'Disconnect WhatsApp',
      content: 'Remove your phone number and stop receiving alerts? Your per-event preferences and per-view mute settings will be kept for next time.',
      okText: 'Disconnect',
      okButtonProps: { danger: true },
      cancelText: 'Cancel',
      onOk: async () => {
        setDisconnecting(true);
        try {
          await updateDoc(doc(db, 'users', effectiveUid), {
            'whatsapp.phoneNumber': deleteField(),
            'whatsapp.verified': false,
          });
          setPhoneInput('');
          setTestSent(false);
          setEditingNumber(false);
          message.success('WhatsApp disconnected');
        } catch (error: any) {
          message.error(error.message || 'Failed to disconnect');
        } finally {
          setDisconnecting(false);
        }
      },
    });
  };

  const handleToggleEvent = async (eventKeys: string[], enabled: boolean) => {
    if (!effectiveUid) return;
    const updates: Record<string, boolean> = {};
    for (const key of eventKeys) {
      updates[`whatsapp.enabledEvents.${key}`] = enabled;
    }
    try {
      await updateDoc(doc(db, 'users', effectiveUid), updates);
    } catch (error: any) {
      message.error('Failed to update preference');
    }
  };

  const isVerified = whatsappData.verified === true;
  const showSetup = !isVerified || editingNumber;
  const enabledEvents = whatsappData.enabledEvents || {};

  return (
    <Modal
      title={
        <Space>
          <MaterialIcon name="notifications" size={20} />
          <span>WhatsApp Alerts</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
    >
      {/* Phone verification section */}
      {showSetup ? (
        <div>
          {/* Back arrow — only when changing number while a verified one exists */}
          {isVerified && editingNumber && (
            <div style={{ marginBottom: 12 }}>
              <Button
                type="text"
                size="small"
                icon={<MaterialIcon name="arrow_back" size={18} />}
                onClick={() => {
                  setEditingNumber(false);
                  setTestSent(false);
                  setPhoneInput(whatsappData.phoneNumber || '');
                }}
              >
                Back
              </Button>
            </div>
          )}
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Receive real-time alerts when your camera disconnects, battery runs low, or device goes offline.
          </Text>

          <Space.Compact style={{ width: '100%', marginBottom: 12 }}>
            <Input
              placeholder="+393331234567"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              style={{ flex: 1 }}
            />
            <Button
              type="primary"
              onClick={handleSendTest}
              loading={sendingTest}
            >
              Send test
            </Button>
          </Space.Compact>

          {testSent && (
            <div>
              <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                A test alert was sent to your WhatsApp. Did you receive it?
              </Text>
              <Button
                type="primary"
                onClick={handleConfirm}
                loading={confirming}
                block
              >
                Yes, I received it — activate alerts
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Compact connected status row (replaces the prominent green banner) */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
              padding: '8px 12px',
              marginBottom: 12,
              border: '1px solid #f0f0f0',
              borderRadius: 6,
              background: '#fafafa',
            }}
          >
            <Tag color="success" style={{ marginInlineEnd: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MaterialIcon name="check_circle" size={12} />
              Connected
            </Tag>
            <Text style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {whatsappData.phoneNumber}
            </Text>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <Button
                size="small"
                onClick={() => {
                  setEditingNumber(true);
                  setPhoneInput(whatsappData.phoneNumber || '');
                  setTestSent(false);
                }}
              >
                Change
              </Button>
              <Button
                size="small"
                danger
                loading={disconnecting}
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            </div>
          </div>

          {/* One switch per event group — toggling a group enables/disables
              all underlying Firestore keys together. */}
          {EVENT_SECTIONS.map((section) => (
            <div key={section.label}>
              <Divider plain style={{ margin: '16px 0 8px' }}>
                <Text type="secondary" style={{ fontSize: 12 }}>{section.label}</Text>
              </Divider>
              {section.events.map((ev) => (
                <div
                  key={ev.keys[0]}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '6px 0',
                  }}
                >
                  <Space size={8}>
                    <MaterialIcon name={ev.icon} size={18} style={{ opacity: 0.65 }} />
                    <Text>{ev.label}</Text>
                  </Space>
                  <Switch
                    size="small"
                    checked={ev.keys.every((k) => enabledEvents[k])}
                    onChange={(checked) => handleToggleEvent(ev.keys, checked)}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
